import { AsyncStatus, SortDirection } from 'lib-shared-legacy-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSetState } from 'react-use';

import {
  ChangeParams,
  GetItems,
  StandardParams,
  UpdateItemLocally,
  UsePaginatedEndpointOptions,
  UsePaginatedEndpointResult,
} from './types';

const DEFAULT_LIMIT = 25;

/**
 * A hook that returns a list of items. It supports pagination & searching.
 *
 * @deprecated Use `usePaginatedEndpoint` or `useLazyPaginatedEndpoint` from
 *   `src/utils/apis/pagination` instead.
 * @param endpoint The api call to get the items
 * @param selectItems A function to select the items from the response
 * @param selectTotal A function to select the total number of items from the
 *   response
 * @param infinite Whether the new results should be appended/prepended to the
 *   list, for infinite lists
 * @param initialParams Initial parameters for searching, filtering
 */
export const usePaginatedEndpoint = <
  TItem,
  TResponseData,
  TSortKey extends string | null,
  TParams extends StandardParams<TSortKey>,
>({
  endpoint,
  itemsSelector,
  totalSelector,
  infinite = false,
  resetSortOnSearch = false,
  initialParams,
}: UsePaginatedEndpointOptions<
  TItem,
  TResponseData,
  TSortKey,
  TParams
>): UsePaginatedEndpointResult<TItem, TSortKey, TParams> => {
  // Data
  const [items, setItems] = useState<TItem[]>([]);
  const [asyncStatus, setAsyncStatus] = useState<AsyncStatus>('succeeded');
  // Pagination
  const [total, setTotal] = useState<number | undefined>();
  const [nextPagePath, setNextPagePath] = useState<string | null>(null);
  const [previousPagePath, setPreviousPagePath] = useState<string | null>(null);
  // Filtering
  const [params, setParams] = useSetState<TParams>({
    ...initialParams,
    limit: initialParams.limit ?? DEFAULT_LIMIT,
    search: initialParams.search ?? '',
    sortKey: initialParams.sortKey ?? null,
    sortDirection: initialParams?.sortDirection ?? SortDirection.Ascending,
  } as TParams);

  const getItems = useCallback<GetItems>(
    async ({ path, mergeStrategy = 'replace' } = {}) => {
      setAsyncStatus('loading');

      try {
        const { sortKey, sortDirection, ...rest } = params;
        const sort_by =
          sortKey === null || (resetSortOnSearch && rest.search)
            ? ''
            : `${sortKey}:${sortDirection}`;
        const { data, meta } = await (path
          ? endpoint({ path })
          : endpoint({
              params: {
                ...rest,
                sort_by,
              },
            }));

        let newList = itemsSelector(data);
        if (mergeStrategy === 'append') {
          newList = [...items, ...newList];
        } else if (mergeStrategy === 'prepend') {
          newList = [...newList, ...items];
        }
        setItems(newList);
        if (totalSelector) {
          setTotal(totalSelector(data));
        }
        setAsyncStatus('succeeded');
        setNextPagePath(meta.next_page);
        setPreviousPagePath(meta.prev_page);
      } catch (error) {
        setAsyncStatus('failed');
      }
    },
    [params, resetSortOnSearch, endpoint, itemsSelector, totalSelector, items],
  );

  const timeoutRef = useRef<number>();
  const skipFetchRef = useRef<boolean>(false);
  const debounceTimeRef = useRef<number>(0);

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    if (skipFetchRef.current) return;

    // When params are changed, the list needs to be replaced with the new results, hence mergeStrategy = "replace"
    const updateList = () => getItems({ mergeStrategy: 'replace' });

    if (debounceTimeRef.current > 0) {
      timeoutRef.current = window.setTimeout(
        updateList,
        debounceTimeRef.current,
      );
    } else {
      updateList();
    }

    // Excluding getItems from the dependencies arrary to prevent an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const changeParams = useCallback<ChangeParams<TParams>>(
    (newParams, { skipFetch = false, debounceTime = 0 } = {}) => {
      skipFetchRef.current = skipFetch;
      debounceTimeRef.current = debounceTime;
      setParams((prevParams) => {
        const newState = { ...prevParams, ...newParams };
        if (resetSortOnSearch && newState.search) newState.sortKey = null;
        return newState;
      });
    },
    [resetSortOnSearch, setParams],
  );

  const nextPage = useCallback(() => {
    if (!nextPagePath) {
      throw Error('Next page does not exist');
    }
    return getItems({
      path: nextPagePath,
      mergeStrategy: infinite ? 'append' : 'replace',
    });
  }, [getItems, infinite, nextPagePath]);

  const previousPage = useCallback(() => {
    if (!previousPagePath) {
      throw Error('Previous page does not exist');
    }
    return getItems({
      path: previousPagePath,
      mergeStrategy: infinite ? 'prepend' : 'replace',
    });
  }, [getItems, infinite, previousPagePath]);

  const updateItem = useCallback<UpdateItemLocally<TItem>>(
    (itemToUpdate, compareFn) => {
      if (!items.includes(itemToUpdate)) {
        setItems(
          items.map((item) =>
            compareFn(item, itemToUpdate) ? itemToUpdate : item,
          ),
        );
      }
    },
    [items],
  );
  const refreshItems = useCallback(() => {
    return getItems({
      mergeStrategy: 'replace',
    });
  }, [getItems]);

  return {
    items,
    total,
    asyncStatus,
    params,
    hasNextPage: nextPagePath != null,
    hasPreviousPage: previousPagePath != null,
    nextPage,
    previousPage,
    changeParams,
    updateItem,
    refreshItems,
  };
};
